// AudioAdapter — Default implementation for audio playback
// Injected into VNInvestCharts to avoid direct window.AudioContext access
// All global access is lazy-initialized inside the factory function for tree-shaking
// ============================================
// Default Audio Adapter (uses Web Audio API)
// ============================================
export const createDefaultAudioAdapter = () => {
    let audioContext = null;
    const getAudioContext = () => {
        if (typeof window === 'undefined')
            return null;
        if (audioContext)
            return audioContext;
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                audioContext = new AudioContextClass();
                return audioContext;
            }
        }
        catch {
            // AudioContext not available
        }
        return null;
    };
    return {
        playAlertSound: (frequency = 880, duration = 300, volume = 0.5) => {
            const ctx = getAudioContext();
            if (!ctx)
                return;
            try {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.frequency.setValueAtTime(frequency, ctx.currentTime);
                gain.gain.setValueAtTime(volume, ctx.currentTime);
                osc.start();
                osc.stop(ctx.currentTime + duration / 1000);
            }
            catch (err) {
                console.warn('AudioAdapter: Failed to play alert sound', err);
            }
        },
        playCustomSound: (audioBuffer) => {
            const ctx = getAudioContext();
            if (!ctx)
                return;
            try {
                const source = ctx.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(ctx.destination);
                source.start();
            }
            catch (err) {
                console.warn('AudioAdapter: Failed to play custom sound', err);
            }
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultAudioAdapter = null;
export const getDefaultAudioAdapter = () => {
    if (!_defaultAudioAdapter) {
        _defaultAudioAdapter = createDefaultAudioAdapter();
    }
    return _defaultAudioAdapter;
};
// Backward compatibility - lazy getter
export const defaultAudioAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultAudioAdapter();
        return adapter[prop];
    }
});
export const createMockAudioAdapter = () => {
    const adapter = {
        playAlertSound: (frequency = 880, duration = 300, volume = 0.5) => {
            adapter.playAlertSoundCalls.push({ frequency, duration, volume });
        },
        playCustomSound: (audioBuffer) => {
            adapter.playCustomSoundCalls.push(audioBuffer);
        },
        playAlertSoundCalls: [],
        playCustomSoundCalls: [],
        resetCalls: () => {
            adapter.playAlertSoundCalls = [];
            adapter.playCustomSoundCalls = [];
        },
    };
    return adapter;
};
// ============================================
// No-op Audio Adapter (for SSR/headless)
// ============================================
export const createNoopAudioAdapter = () => ({
    playAlertSound: () => { },
    playCustomSound: () => { },
});
